import { Injectable, inject } from "@angular/core";
import {
	Firestore,
	collection,
	addDoc,
	serverTimestamp,
	query,
	where,
	orderBy,
	getDocs,
	doc,
	updateDoc,
} from "@angular/fire/firestore";
import { Feedback } from "../models/feedback.model";
import { Auth } from "@angular/fire/auth";
import { Observable, from, map } from "rxjs";

@Injectable({
	providedIn: "root",
})
export class FeedbackService {
	private firestore = inject(Firestore);
	private auth = inject(Auth);

	async submitFeedback(
		feedback: Omit<Feedback, "id" | "createdAt" | "updatedAt">,
	): Promise<string> {
		const user = this.auth.currentUser;
		if (!user) {
			throw new Error("User must be logged in to submit feedback");
		}

		const feedbackData = {
			...feedback,
			user_id: user.uid, // Use snake_case
			user_email: user.email || "unknown", // Use snake_case
			created_at: new Date(), // Use snake_case
			updated_at: new Date(), // Use snake_case
			jira_id: "unassigned", // Use snake_case
		};

		const docRef = await addDoc(collection(this.firestore, "feedback"), {
			...feedbackData,
			created_at: serverTimestamp(), // Use snake_case
			updated_at: serverTimestamp(), // Use snake_case
		});

		return docRef.id;
	}

	getFeedback(): Observable<Feedback[]> {
		const feedbackRef = collection(this.firestore, "feedback");
		const q = query(feedbackRef, orderBy("created_at", "desc")); // Use snake_case

		return from(getDocs(q)).pipe(
			map((snapshot) => {
				const feedbackList = snapshot.docs.map((doc) => {
					const data = doc.data();
					// Map snake_case fields to camelCase for the model
					return {
						id: doc.id,
						userId: data["user_id"],
						userEmail: data["user_email"],
						route: data["route"],
						description: data["description"],
						type: data["type"],
						status: data["status"],
						createdAt: data["created_at"]?.toDate?.() || new Date(),
						updatedAt: data["updated_at"]?.toDate?.() || new Date(),
						jiraId: data["jira_id"],
					} as Feedback;
				});

				// Sort by status with open at the top, then by created date
				const statusOrder = {
					open: 0,
					"in-progress": 1,
					done: 2,
					closed: 2, // Treat 'done' and 'closed' with same priority
				};

				return feedbackList.sort((a, b) => {
					const statusDiff =
						(statusOrder[a.status] || 999) - (statusOrder[b.status] || 999);
					if (statusDiff !== 0) return statusDiff;
					// If status is the same, sort by created date (newest first)
					return b.createdAt.getTime() - a.createdAt.getTime();
				});
			}),
		);
	}

	updateFeedbackStatus(
		feedbackId: string,
		status: "open" | "in-progress" | "done" | "closed",
	): Observable<void> {
		const feedbackRef = doc(this.firestore, "feedback", feedbackId);
		return from(
			updateDoc(feedbackRef, {
				status,
				updated_at: serverTimestamp(), // Use snake_case
			}),
		);
	}
}
